/**
 * TTS actions: run a single QueueItem in the DOM (highlight, click, pause, emoji, type, etc.).
 * Used by the TTS feature when processing the queue.
 */

import { type QueueItem, DEFAULT_TTS_FADEOUT_MS } from './tts-queue';
import {
  playTtsSoundBlocking,
  playTtsSoundNonBlocking,
  stopTtsSound,
  fadeOutTtsSound
} from './tts-sound';
import { startPauseBreak, setPauseScreen } from './pause-break';

export function getElementsForSelector(root: ParentNode, selector: string | string[]): Element[] {
  const list: Element[] = [];
  if (Array.isArray(selector)) {
    for (const sel of selector) {
      try {
        const el = root.querySelector(sel);
        if (el) list.push(el);
      } catch {}
    }
  } else if (typeof selector === 'string' && selector) {
    try {
      const el = root.querySelector(selector);
      if (el) list.push(el);
    } catch {}
  }
  return list;
}

export interface RunActionOptions {
  /** Mutable array of elements with persistent highlight (starthighlight); cleared/updated by runAction. */
  activePersistentHighlights: Element[];
  zoomRuntime: {
    level: number;
    autoResetTimer: ReturnType<typeof setTimeout> | null;
    previousBodyStyles: {
      transform: string;
      transformOrigin: string;
      transition: string;
      zoom: string;
      position: string;
      left: string;
      top: string;
      width: string;
      height: string;
    } | null;
  };
}

export function runAction(
  item: QueueItem,
  slideEl: HTMLElement | null,
  done: () => void,
  options: RunActionOptions
): void {
  const { activePersistentHighlights, zoomRuntime } = options;
  const root = slideEl || document;
  const value = 'selector' in item ? item.selector : null;
  const elements = value != null ? getElementsForSelector(root as ParentNode, value) : [];
  const fallbackElements = elements.length === 0 && slideEl ? getElementsForSelector(document, value as string | string[]) : elements;
  const elList = fallbackElements.length ? fallbackElements : elements;
  const stophighlightNoSelector = item.type === 'stophighlight' && (!value || (Array.isArray(value) && value.length === 0));

  function logSelectorWarning(): void {
    const sel = value == null ? '' : Array.isArray(value) ? value.join(', ') : String(value);
    const scope = slideEl ? 'current slide or document' : 'document';
    console.warn(
      '[Lectura TTS] Codeword "%s": no element found for selector "%s" (%s). Check slide notes and fix the selector.',
      item.type,
      sel || '(empty)',
      scope
    );
  }

  function clearPersistentHighlights(): void {
    for (const el of activePersistentHighlights) {
      el.classList.remove('notes-tts-highlight-on');
    }
    activePersistentHighlights.length = 0;
  }

  function getChatToggleEl(): HTMLElement | null {
    return document.querySelector('.lectura-bar-chat button, .lectura-bar-chat');
  }

  function getChatInputEl(): HTMLTextAreaElement | null {
    const el = document.querySelector('.rf-chat-input');
    return el instanceof HTMLTextAreaElement ? el : null;
  }

  function setChatDraft(text: string): void {
    const input = getChatInputEl();
    if (!input) return;
    input.value = text;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
  }

  function zoomOutNow(): void {
    const revealMaybe = (typeof Reveal !== 'undefined' ? Reveal : undefined) as { getRevealElement?: () => Element | null } | undefined;
    const revealEl = revealMaybe?.getRevealElement?.();
    if (revealEl) {
      // RevealZoom listens for Escape to reset zoom state.
      document.dispatchEvent(new KeyboardEvent('keyup', { key: 'Escape', keyCode: 27, which: 27, bubbles: true }));
    }
    if (zoomRuntime.autoResetTimer) {
      clearTimeout(zoomRuntime.autoResetTimer);
      zoomRuntime.autoResetTimer = null;
    }
    const prev = zoomRuntime.previousBodyStyles;
    if (!prev) return;
    document.body.style.transform = prev.transform;
    document.body.style.transformOrigin = prev.transformOrigin;
    document.body.style.transition = prev.transition;
    document.body.style.zoom = prev.zoom;
    document.body.style.position = prev.position;
    document.body.style.left = prev.left;
    document.body.style.top = prev.top;
    document.body.style.width = prev.width;
    document.body.style.height = prev.height;
    document.documentElement.classList.remove('zoomed');
    zoomRuntime.level = 1;
    zoomRuntime.previousBodyStyles = null;
  }

  function normalizeComboToken(token: string): string {
    const t = token.trim().toLowerCase();
    if (t === 'ctrl' || t === 'control') return 'Control';
    if (t === 'shift') return 'Shift';
    if (t === 'alt' || t === 'option') return 'Alt';
    if (t === 'meta' || t === 'cmd' || t === 'command' || t === 'win') return 'Meta';
    if (t === 'esc') return 'Escape';
    if (t === 'enter' || t === 'return') return 'Enter';
    if (t === 'tab') return 'Tab';
    if (t === 'space' || t === 'spacebar') return ' ';
    if (t === 'slash') return '/';
    if (t.length === 1) {
      if (t >= 'a' && t <= 'z') return t.toUpperCase();
      return t;
    }
    return token.trim();
  }

  function dispatchKeyboardCombo(rawCombo: string): void {
    const tokens = String(rawCombo || '')
      .split('+')
      .map((t) => normalizeComboToken(t))
      .filter(Boolean);
    if (tokens.length === 0) return;

    const held = new Set<string>();
    const isModifier = (k: string): boolean => k === 'Control' || k === 'Shift' || k === 'Alt' || k === 'Meta';
    const activeEl = document.activeElement as HTMLElement | null;
    let targetEl = (activeEl && activeEl !== document.body ? activeEl : document) as EventTarget;

    function toKeyCode(key: string): number {
      if (key === 'Enter') return 13;
      if (key === 'Tab') return 9;
      if (key === 'Escape') return 27;
      if (key === ' ') return 32;
      if (key.length === 1) return key.toUpperCase().charCodeAt(0);
      return 0;
    }

    function flags() {
      return {
        ctrlKey: held.has('Control'),
        shiftKey: held.has('Shift'),
        altKey: held.has('Alt'),
        metaKey: held.has('Meta'),
        bubbles: true,
        cancelable: true
      };
    }

    function dispatchKeyEvent(type: 'keydown' | 'keyup', key: string): void {
      const code = toKeyCode(key);
      const evt = new KeyboardEvent(type, { key, ...flags() });
      try {
        Object.defineProperty(evt, 'keyCode', { configurable: true, get: () => code });
        Object.defineProperty(evt, 'which', { configurable: true, get: () => code });
      } catch {
        // ignore; best-effort for older keyCode listeners
      }
      targetEl.dispatchEvent(evt);
    }

    const ordered = [
      ...tokens.filter((k) => isModifier(k)),
      ...tokens.filter((k) => !isModifier(k))
    ];

    // If Enter is requested while RevealSearch input is visible, make sure Enter goes there.
    if (ordered.length === 1 && ordered[0] === 'Enter') {
      const searchInput = document.querySelector('.searchbox .searchinput') as HTMLInputElement | null;
      const searchBox = document.querySelector('.searchbox') as HTMLElement | null;
      const searchVisible = !!searchBox && window.getComputedStyle(searchBox).display !== 'none';
      if (searchInput && searchVisible) {
        searchInput.focus();
        targetEl = searchInput;
      }
    }

    for (const key of ordered) {
      if (isModifier(key)) held.add(key);
      dispatchKeyEvent('keydown', key);
    }
    for (let i = ordered.length - 1; i >= 0; i--) {
      const key = ordered[i]!;
      dispatchKeyEvent('keyup', key);
      if (isModifier(key)) held.delete(key);
    }
  }

  function zoomIntoElement(targetEl: Element, level: number): void {
    const revealMaybe = (typeof Reveal !== 'undefined' ? Reveal : undefined) as {
      getRevealElement?: () => Element | null;
      configure?: (opts: Record<string, unknown>) => void;
    } | undefined;
    const revealEl = revealMaybe?.getRevealElement?.();
    if (revealEl) {
      // Use RevealZoom's own mousedown handler so centering math matches native plugin behavior.
      try {
        revealMaybe?.configure?.({ zoomLevel: level });
      } catch {}
      const bounds = targetEl.getBoundingClientRect();
      const centerX = Math.round(bounds.left + bounds.width / 2);
      const centerY = Math.round(bounds.top + bounds.height / 2);
      const evt = new MouseEvent('mousedown', {
        bubbles: true,
        cancelable: true,
        clientX: centerX,
        clientY: centerY,
        altKey: true,
        ctrlKey: true
      });
      revealEl.dispatchEvent(evt);
      zoomRuntime.level = level;
      return;
    }

    // Fallback path when RevealZoom plugin is not available.
    if (!zoomRuntime.previousBodyStyles) {
      zoomRuntime.previousBodyStyles = {
        transform: document.body.style.transform || '',
        transformOrigin: document.body.style.transformOrigin || '',
        transition: document.body.style.transition || '',
        zoom: document.body.style.zoom || '',
        position: document.body.style.position || '',
        left: document.body.style.left || '',
        top: document.body.style.top || '',
        width: document.body.style.width || '',
        height: document.body.style.height || ''
      };
    }
    const bounds = targetEl.getBoundingClientRect();
    const padding = 20;
    const width = Math.max(1, bounds.width + padding * 2);
    const height = Math.max(1, bounds.height + padding * 2);
    let x = bounds.left - padding;
    let y = bounds.top - padding;
    x -= (window.innerWidth - width * level) / 2;
    y -= (window.innerHeight - height * level) / 2;
    const origin = `${window.scrollX}px ${window.scrollY}px`;
    document.body.style.transition = 'transform 0.45s ease';
    document.body.style.transformOrigin = origin;
    document.body.style.transform = `translate(${-x}px,${-y}px) scale(${level})`;
    document.documentElement.classList.add('zoomed');
    zoomRuntime.level = level;
  }

  if (item.type === 'highlight' || item.type === 'starthighlight' || item.type === 'zoom' || item.type === 'zoomin' || item.type === 'click' || item.type === 'focus' || item.type === 'scroll' || item.type === 'waitclick' || item.type === 'play' || item.type === 'pausevideo' || item.type === 'type') {
    if (elList.length === 0) {
      logSelectorWarning();
      done();
      return;
    }
  }
  if (item.type === 'stophighlight' && value && elList.length === 0 && !stophighlightNoSelector) {
    logSelectorWarning();
    done();
    return;
  }

  switch (item.type) {
    case 'highlight': {
      const cls = item.ring ? 'notes-tts-highlight-ring' : 'notes-tts-highlight';
      const duration = item.ring ? 6000 : 4500;
      for (const el of elList) {
        el.classList.add(cls);
        setTimeout(() => {
          el.classList.remove(cls);
        }, duration);
      }
      done();
      return;
    }
    case 'starthighlight':
      for (const el of elList) {
        el.classList.add('notes-tts-highlight-on');
        activePersistentHighlights.push(el);
      }
      break;
    case 'stophighlight':
      if (stophighlightNoSelector) {
        clearPersistentHighlights();
      } else {
        for (const el of elList) {
          el.classList.remove('notes-tts-highlight-on');
          const idx = activePersistentHighlights.indexOf(el);
          if (idx !== -1) activePersistentHighlights.splice(idx, 1);
        }
      }
      break;
    case 'zoom': {
      const target = elList[0];
      if (target) zoomIntoElement(target, Math.max(1.1, item.level || 2));
      const dur = Math.max(300, Math.min(item.ms || 2500, 30000));
      if (zoomRuntime.autoResetTimer) clearTimeout(zoomRuntime.autoResetTimer);
      zoomRuntime.autoResetTimer = setTimeout(() => zoomOutNow(), dur);
      break;
    }
    case 'zoomin': {
      const target = elList[0];
      if (target) zoomIntoElement(target, Math.max(1.1, item.level || 2));
      if (zoomRuntime.autoResetTimer) {
        clearTimeout(zoomRuntime.autoResetTimer);
        zoomRuntime.autoResetTimer = null;
      }
      break;
    }
    case 'zoomout':
      zoomOutNow();
      break;
    case 'keypress':
      dispatchKeyboardCombo(item.combo || '');
      break;
    case 'click': {
      const clickDuration = 650;
      for (const el of elList) {
        (el as HTMLElement).classList.add('notes-tts-click-flash');
        (el as HTMLElement).click();
        setTimeout(() => (el as HTMLElement).classList.remove('notes-tts-click-flash'), clickDuration);
      }
      setTimeout(done, clickDuration);
      return;
    }
    case 'focus':
      if (elList.length) (elList[0] as HTMLElement).focus();
      break;
    case 'scroll':
      /* nearest: scrolls overflow ancestors (e.g. Wireshark-style panes) before the whole slide jumps */
      if (elList.length)
        (elList[0] as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      break;
    case 'waitclick': {
      const target = elList[0] as HTMLElement;
      function onWaitClick(): void {
        target.removeEventListener('click', onWaitClick);
        done();
      }
      target.addEventListener('click', onWaitClick);
      break;
    }
    case 'play':
      for (const el of elList) {
        // HTML5 media (video/audio)
        if ((el as HTMLMediaElement).play) {
          (el as HTMLMediaElement).play().catch(() => {});
          continue;
        }

        // YouTube iframe (requires `enablejsapi=1` in the iframe URL)
        if (el instanceof HTMLIFrameElement && el.contentWindow) {
          try {
            el.contentWindow.postMessage(
              JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
              '*',
            );
          } catch {
            // ignore
          }
        }
      }
      // When starting a video, do not advance TTS until presenter clicks.
      // Note: clicks inside cross-origin iframes won't reach the parent document,
      // so for iframes we also accept a click on the iframe's parent wrapper.
      {
        const primary = elList[0] as HTMLElement;
        const target =
          primary instanceof HTMLIFrameElement ? ((primary.parentElement as HTMLElement) || primary) : primary;
        function onWaitClickAfterPlay(): void {
          target.removeEventListener('click', onWaitClickAfterPlay);
          done();
        }
        target.addEventListener('click', onWaitClickAfterPlay);
      }
      return;
    case 'pausevideo':
      for (const el of elList) {
        // HTML5 media (video/audio)
        if (typeof (el as HTMLMediaElement).pause === 'function') {
          (el as HTMLMediaElement).pause();
          continue;
        }

        // YouTube iframe (requires `enablejsapi=1` in the iframe URL)
        if (el instanceof HTMLIFrameElement && el.contentWindow) {
          try {
            el.contentWindow.postMessage(
              JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
              '*',
            );
          } catch {
            // ignore
          }
        }
      }
      break;
    case 'sound': {
      playTtsSoundBlocking(item.key, () => done(), item.volume);
      return;
    }
    case 'soundasync': {
      playTtsSoundNonBlocking(item.key, item.volume);
      done();
      return;
    }
    case 'stopsound': {
      stopTtsSound();
      done();
      return;
    }
    case 'fadeoutsound': {
      fadeOutTtsSound(item.ms ?? DEFAULT_TTS_FADEOUT_MS, () => done());
      return;
    }
    case 'pausebreak': {
      startPauseBreak(item.minutes);
      done();
      return;
    }
    case 'emoji': {
      const emojiChar = item.value || '😊';
      const revealContainer = document.querySelector<HTMLElement>('.rf-slides-chat-wrap .reveal, .reveal');
      if (!revealContainer) {
        done();
        break;
      }
      const pos = window.getComputedStyle(revealContainer).position;
      if (pos === 'static') revealContainer.style.position = 'relative';
      const overlay = document.createElement('div');
      overlay.className = 'notes-tts-emoji-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.textContent = emojiChar;
      revealContainer.appendChild(overlay);
      setTimeout(() => {
        overlay.classList.add('notes-tts-emoji-out');
        setTimeout(() => {
          overlay.remove();
          done();
        }, 400);
      }, 2200);
      break;
    }
    case 'type': {
      const targetEl = elList[0] as HTMLInputElement | HTMLTextAreaElement | HTMLElement;
      const textToType = item.text || '';
      const delayPerChar = 50;
      const setVal = (el: HTMLInputElement | HTMLTextAreaElement | HTMLElement, str: string): void => {
        if ((el as HTMLElement).contentEditable === 'true') {
          (el as HTMLElement).textContent = str;
          el.dispatchEvent(new Event('input', { bubbles: true }));
        } else if ('value' in el && typeof (el as HTMLInputElement).value !== 'undefined') {
          (el as HTMLInputElement).value = str;
          el.dispatchEvent(new Event('input', { bubbles: true }));
          const dirtyEvt = new KeyboardEvent('keyup', { key: 'A', bubbles: true, cancelable: true });
          try {
            Object.defineProperty(dirtyEvt, 'keyCode', { configurable: true, get: () => 65 });
            Object.defineProperty(dirtyEvt, 'which', { configurable: true, get: () => 65 });
          } catch {}
          el.dispatchEvent(dirtyEvt);
        }
      };
      setVal(targetEl, '');
      let idx = 0;
      function typeNext(): void {
        if (idx >= textToType.length) {
          done();
          return;
        }
        setVal(targetEl, textToType.slice(0, idx + 1));
        idx++;
        setTimeout(typeNext, delayPerChar);
      }
      setTimeout(typeNext, delayPerChar);
      return;
    }
    case 'chatopen': {
      const api = (window as Window & { LecturaNext?: { open?: () => void } }).LecturaNext;
      if (api && typeof api.open === 'function') {
        api.open();
      } else {
        const toggle = getChatToggleEl();
        if (toggle) toggle.click();
      }
      const input = getChatInputEl();
      if (input) input.focus();
      done();
      return;
    }
    case 'chatdraft': {
      const msg = String(item.text || '').trim();
      if (!msg) {
        done();
        return;
      }
      const api = (window as Window & { LecturaNext?: { openChatWithDraft?: (text: string) => void } }).LecturaNext;
      if (api && typeof api.openChatWithDraft === 'function') {
        api.openChatWithDraft(msg);
        done();
        return;
      }
      const toggle = getChatToggleEl();
      if (toggle) toggle.click();
      // Drawer open is async; prefill on next tick.
      setTimeout(() => {
        setChatDraft(msg);
        done();
      }, 120);
      return;
    }
    case 'chatcleardraft': {
      setChatDraft('');
      done();
      return;
    }
    case 'chatclose': {
      const api = (window as Window & { LecturaNext?: { close?: () => void } }).LecturaNext;
      if (api && typeof api.close === 'function') {
        api.close();
        done();
        return;
      }
      const toggle = getChatToggleEl();
      if (toggle) toggle.click();
      done();
      return;
    }
    case 'pauseon': {
      setPauseScreen(true);
      done();
      return;
    }
    case 'pauseoff': {
      setPauseScreen(false);
      done();
      return;
    }
    case 'qrshow': {
      const o = document.getElementById('rf-qr-overlay');
      const open = !!o?.classList.contains('rf-qr-overlay-open');
      if (!open) {
        document.querySelector<HTMLButtonElement>('.lectura-bar-qr-btn')?.click();
      }
      done();
      return;
    }
    case 'qrhide': {
      const o = document.getElementById('rf-qr-overlay');
      const open = !!o?.classList.contains('rf-qr-overlay-open');
      if (open) {
        document.querySelector<HTMLButtonElement>('.lectura-bar-qr-btn')?.click();
      }
      done();
      return;
    }
    case 'qrbig': {
      const o = document.getElementById('rf-qr-overlay');
      const open = !!o?.classList.contains('rf-qr-overlay-open');
      if (!open) {
        document.querySelector<HTMLButtonElement>('.lectura-bar-qr-btn')?.click();
      }
      let attempts = 0;
      const maxAttempts = 60;
      const tick = (): void => {
        const link = document.querySelector('#rf-qr-overlay .lectura-qr-link') as HTMLElement | null;
        if (link) {
          // Use the same toggle as a direct click on the QR (closure in renderQrOverlayContent).
          link.click();
          done();
          return;
        }
        if (attempts++ < maxAttempts) {
          setTimeout(tick, 100);
        } else {
          done();
        }
      };
      setTimeout(tick, 0);
      return;
    }
    default:
      break;
  }
  const t = (item as QueueItem).type;
  const needDone =
    t !== 'waitclick' &&
    t !== 'play' &&
    t !== 'emoji' &&
    t !== 'type' &&
    t !== 'chatopen' &&
    t !== 'chatdraft' &&
    t !== 'chatcleardraft' &&
    t !== 'chatclose' &&
    t !== 'sound' &&
    t !== 'soundasync' &&
    t !== 'stopsound' &&
    t !== 'fadeoutsound' &&
    t !== 'pausebreak' &&
    t !== 'pauseon' &&
    t !== 'pauseoff' &&
    t !== 'qrshow' &&
    t !== 'qrhide' &&
    t !== 'qrbig';
  if (needDone) done();
}
